import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { SearchField } from '../../../../../shared/ui/search-field';
import { ShowMoreToggle } from '../../../../../shared/ui/show-more-toggle';
import { NoResultsText } from '../../../../../shared/ui/no-results-text';
import { Tooltip } from '../../../../../shared/ui/tooltip';
import { IconButton } from '../../../../../shared/ui/icon-button';
import { InfoCircleMutedIcon } from '../../../../../shared/ui/icons';
import { getVisibleTagOptions, normalizeFilterSearchValue } from '../lib/filterHelpers';
import styles from '../FiltersPanel.module.css';
import { FilterOption } from './FilterOption';
import { TagsModeToggle } from './TagsModeToggle';

const MODE_HINT = (
  <>
    <strong>Include:</strong> Show only templates with the selected tags.
    <br />
    <br />
    <strong>Exclude:</strong> Hide templates with the selected tags.
  </>
);

export function TagsSection({
  tags = [],
  filters,
  mode = 'include',
  onModeChange,
  onToggle,
  searchValue,
  onSearchChange,
}) {
  const [tagsExpanded, setTagsExpanded] = useState(false);

  const normalizedSearchValue = normalizeFilterSearchValue(searchValue);
  const visibleTags = useMemo(
    () =>
      getVisibleTagOptions({
        tags,
        normalizedSearchValue,
        tagsExpanded,
      }),
    [tags, normalizedSearchValue, tagsExpanded],
  );

  const optionTone = mode === 'exclude' ? 'danger' : 'default';

  return (
    <section className={clsx(styles.tagsSection)}>
      <div className={clsx(styles.tagsHeader)}>
        <div className={clsx(styles.tagsHeaderRow)}>
          <h3 className={clsx(styles.sectionTitle)}>Tags</h3>
          <TagsModeToggle mode={mode} onChange={onModeChange} />
          <Tooltip content={MODE_HINT} placement="top" wrap="normal" bubbleWidthPx={258}>
            <IconButton
              variant="plain"
              aria-label="Tags filter mode info"
              className={clsx(styles.tagsHintButton)}
            >
              <InfoCircleMutedIcon />
            </IconButton>
          </Tooltip>
        </div>
        <SearchField
          className={clsx(styles.searchField)}
          ariaLabel="Search tags"
          value={searchValue}
          onChange={onSearchChange}
          onClear={() => {
            onSearchChange('');
            setTagsExpanded(false);
          }}
        />
      </div>

      {normalizedSearchValue.length >= 3 && visibleTags.length === 0 ? (
        <NoResultsText />
      ) : (
        <div className={clsx(styles.options)}>
          {visibleTags.map(({ key, label, count }, index) => (
            <FilterOption
              key={`Tags-${key}-${index}`}
              sectionKey="tags"
              optionKey={key}
              label={label}
              count={count}
              tone={optionTone}
              checked={(filters?.tags ?? []).map(String).includes(String(key))}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}

      {normalizedSearchValue.length < 3 && tags.length > 5 ? (
        <ShowMoreToggle
          expanded={tagsExpanded}
          onToggle={() => setTagsExpanded((current) => !current)}
        />
      ) : null}
    </section>
  );
}
